import { MessagePartKind } from "models/enumerations/conversations/message-part-kind";
import { RawMessagePart } from "models/interfaces/conversations/message-part";
import MessagePartRecord from "models/view-models/conversations/message-part-record";
import SuggestMemoryPartRecord from "models/view-models/conversations/suggest-memory-part-record";

/**
 * Any of the concrete message part records. Immutable records cannot add fields via
 * inheritance, so the polymorphic parts are modeled as a union rather than a base class.
 */
export type AnyMessagePartRecord = MessagePartRecord | SuggestMemoryPartRecord;

/**
 * Converts raw or partially hydrated message parts into their matching record type
 * based on the `kind` discriminator. Unrecognized kinds fall back to the base record.
 */
export function ensureMessagePartRecords(
    parts?: RawMessagePart[]
): AnyMessagePartRecord[] {
    if (parts == null) {
        return [];
    }

    return parts.filter((part) => part != null).map(ensureMessagePartRecord);
}

/**
 * Merges incoming parts into the existing parts, replacing any part that shares a
 * `partId` and appending the rest.
 */
export function upsertMessageParts(
    existingParts?: AnyMessagePartRecord[],
    incomingParts?: AnyMessagePartRecord[]
): AnyMessagePartRecord[] {
    if (incomingParts == null) {
        return existingParts ?? [];
    }

    return incomingParts.reduce<AnyMessagePartRecord[]>((parts, part) => {
        const index =
            part.partId == null
                ? -1
                : parts.findIndex((p) => p.partId === part.partId);

        if (index === -1) {
            return [...parts, part];
        }

        const nextParts = [...parts];
        nextParts[index] = part;

        return nextParts;
    }, existingParts ?? []);
}

function ensureMessagePartRecord(part: RawMessagePart): AnyMessagePartRecord {
    if (
        part instanceof SuggestMemoryPartRecord ||
        part instanceof MessagePartRecord
    ) {
        return part;
    }

    switch (part.kind) {
        case MessagePartKind.SuggestMemory:
            return new SuggestMemoryPartRecord(part);
        default:
            return new MessagePartRecord(part);
    }
}
